/**
 * Client-side mirror of the server's `PasswordRules`.
 *
 * This exists for immediate feedback while typing, not for enforcement — the server
 * validates every password it is sent and is the only authority. Keep the two in step: if
 * `PasswordRules.MinimumLength` or its complexity rule moves, move these with it in the
 * same change, or the form will accept something the API then rejects.
 */

export const PASSWORD_MINIMUM_LENGTH = 12;

export const PASSWORD_COMPLEXITY_MESSAGE =
  'Password must contain an uppercase letter, a lowercase letter, a digit, and a symbol.';

/** The rule stated once, for use as field help text before anything is typed. */
export const PASSWORD_HELP = `At least ${PASSWORD_MINIMUM_LENGTH} characters, with an uppercase letter, a lowercase letter, a digit, and a symbol.`;

/** The first failing rule, or `undefined` when the password is acceptable. */
export function passwordError(password: string): string | undefined {
  if (password.length === 0) {
    return 'Password is required.';
  }

  if (password.length < PASSWORD_MINIMUM_LENGTH) {
    return `Password must be at least ${PASSWORD_MINIMUM_LENGTH} characters.`;
  }

  // Anything that is not a letter or a digit counts as a symbol, matching the server, which
  // classifies by exclusion rather than against a fixed symbol set.
  const hasUpper = /\p{Lu}/u.test(password);
  const hasLower = /\p{Ll}/u.test(password);
  const hasDigit = /\p{Nd}/u.test(password);
  const hasSymbol = /[^\p{Lu}\p{Ll}\p{Nd}]/u.test(password);

  return hasUpper && hasLower && hasDigit && hasSymbol ? undefined : PASSWORD_COMPLEXITY_MESSAGE;
}
